import { Icon } from "armstrong-react";
import classnames from "classnames";
import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { Editor, Inline } from "slate";
import Url from "url-parse";
import { LazyLoadImage, useWindowDimensions } from "../../../../../../core/atoms";
import { ResponsiveBreakpoints } from "../../../../../../../../packages/ui/src/utils/uiHelpers";
import { getNormalisedUrl } from "@travellocal/utils/urlHelpers";
import { HoverMenu } from "../../../hoverMenu/hoverMenu";
import { INLINES } from "../../../html";
import { ToolbarButton } from "../../../toolbar/toolbarButton";
import { getLink, hasLinks, removeLink } from "../linkUtils";
import { EditLinkForm } from "./editLinkForm";

interface Props {
  linkNode: Inline;
  controller: Editor;
}

/**
 * Edit UI for links
 */
export const EditTooltip = ({ linkNode, controller }: Props) => {
  const { value } = controller;
  const { selection } = value;

  // New links inserted by toolbar have different UX
  // - tooltip opens as expanded
  // - link is removed if no form not successfully submitted
  const isNewLink = !!linkNode.data.get("isNew");

  // When selection changes,
  // Component has two visual states - closed and expanded
  const [isEditing, setEditing] = React.useState<boolean>(isNewLink);
  const className = classnames("slate-link-tooltip", {
    "slate-link-tooltip--closed": !isEditing,
    "slate-link-tooltip--expanded": isEditing,
  });

  // Expanded state is fullscreen on mobile
  const { width } = useWindowDimensions();
  const isFullscreen = isEditing && width < ResponsiveBreakpoints.tablet;

  const showTooltip = hasLinks(value)
    && selection.isCollapsed // Hide when text highlighted
    && linkNode.key === getLink(value).key; // Current node is the same as the currently selected link
  if (!showTooltip) {
    return null;
  }

  const onTooltipClose = () => {
    if (isNewLink) {
      controller.unwrapInlineByKey(linkNode.key, { type: INLINES.LINK.type, data: { isNew: true } });
    }
    else {
      setEditing(false);
    }
  };

  const normalisedUrl = getNormalisedUrl(linkNode.data.get("href"));
  const urlDomain = new Url(normalisedUrl || "http://example.com").host;
  const urlWithoutProtocol = (normalisedUrl || "").replace(/.*:\/\//i, "");

  return (
    <HoverMenu
      node={linkNode}
      className={className}
      onClose={onTooltipClose}
      origin={isFullscreen ? "window" : "nodeLeft"}>
      {!isEditing && (
        <>
          <LazyLoadImage
            className="slate-link-tooltip__favicon"
            src={`https://www.google.com/s2/favicons?domain=${urlDomain}`}
            alt={`Favicon for ${urlDomain}`}
          />
          <a className="slate-link-tooltip__href" href={normalisedUrl} target="_blank" title={normalisedUrl}>
            {urlWithoutProtocol}
          </a>

          <CopyToClipboard text={normalisedUrl}>
            <ToolbarButton title="Copy link">
              <Icon icon={Icon.Icomoon.copy3} />
            </ToolbarButton>
          </CopyToClipboard>
          <ToolbarButton onClick={() => setEditing(true)} title="Change link">
            <Icon icon={Icon.Icomoon.pencil} />
          </ToolbarButton>
          <ToolbarButton onClick={() => removeLink(controller)} title="Remove link">
            <Icon icon={Icon.Icomoon.unlink} />
          </ToolbarButton>
        </>
      )}

      {isEditing && (
        <EditLinkForm
          controller={controller}
          linkNode={linkNode}
          onClose={() => setEditing(false)}
        />
      )}
    </HoverMenu>
  );
};
